import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AppConfigService } from './app-config.service';
import { UserSettings } from '../interfaces/user-settings.interface';

const DEFAULT_SETTINGS: UserSettings = {
  insights_enabled: false,
  auto_code_correction: false
};

@Injectable({ providedIn: 'root' })
export class UserSettingsService {
  private readonly http = inject(HttpClient);
  private readonly configService = inject(AppConfigService);
  private readonly _settings = signal<UserSettings>({ ...DEFAULT_SETTINGS });

  /** Read-only signal — inject this in templates and components. */
  readonly settings = this._settings.asReadonly();

  constructor() {
    // Self-loads on first injection. Auth token is already present because
    // the auth interceptor attaches it to every HTTP request automatically.
    this.load();
  }

  private get url(): string {
    return `${this.configService.apiBaseUrl}/api/v2/user/settings`;
  }

  private async load(): Promise<void> {
    const result = await firstValueFrom(
      this.http.get<UserSettings>(this.url)
    ).catch(() => ({ ...DEFAULT_SETTINGS }));
    this._settings.set(result);
  }

  /** Sends only the changed flag(s) to the backend (partial update) and updates the global signal. */
  async update(patch: Partial<UserSettings>): Promise<void> {
    const updated = await firstValueFrom(
      this.http.put<UserSettings>(this.url, patch)
    ).catch(() => ({ ...this._settings(), ...patch }));
    this._settings.set(updated);
  }
}
